import { MoneyOffRounded as MoneyOffRoundedIcon } from 'components/icons';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import Chip from 'components/@extended/Chip.tsx';
import { FormattedDate } from 'components/misc/FormattedDate';
import LineClamp from 'components/misc/LineClamp.tsx';
import { RecordLink } from 'components/record-chip/RecordLink';
import NestedTable, { NestedColumn } from 'components/tables/list-table/NestedTable';
import { Tooltip } from 'components/ui/tooltip';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import MatterIndicators, { BOTTOM_LINE_IN_NEW_DESIGN, hasMatterAccess } from 'components/matters/MatterIndicators';
import MoneyDisplay from 'components/util/MoneyDisplay';
import ExpenseActionButtons from 'pages/matters/expenses/ExpenseActionButtons';
import { getLinks } from 'pages/matters/util';
import TimecardLinkIcon from 'pages/timecards/expenses/TimeCardLinkIcon';
import { ClassifierData } from 'types/classifiers';
import { ExpenseListItem } from 'types/expenses';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { ExpenseStatusLabel } from './ExpenseStatusLabel';
import { getLatestConnectedInvoicePaidDate } from './expensesInvoiceUtil';

/** A link in a cell: the cell's `.entity-link` rule colours it, and it underlines on hover. */
const CELL_LINK = 'entity-link w-fit no-underline hover:underline';

type Props = {
    expenses: ExpenseListItem[];
    expenseStatusClassifier?: ClassifierData;
    onDuplicate: (expense: ExpenseListItem) => void;
};

/**
 * Tier 3 of the grouped Expenses view: individual expense lines for one (expense type, employee) pair.
 * Lines are pre-sorted DESC by date by the backend; the trailing columns mirror the global
 * Expenses list order: Date / Description / Status / Invoice, Paid date / Case, BD effort /
 * Amount / Orig. amount / kebab. The Invoice cell stacks the paid-on date of the latest connected
 * invoice (only when that invoice is PAID) beneath the invoice links, and the date itself links to
 * the expense's page.
 */
const ExpensesGroupedTier3Rows = ({ expenses, expenseStatusClassifier, onDuplicate }: Props) => {
    const columns = useMemo<NestedColumn<ExpenseListItem>[]>(
        () => [
            {
                id: 'expenseDate',
                header: <FormattedMessage id="expenses.date" />,
                width: '12%',
                Cell: (expense) => (
                    <LinkWithOrigin
                        className={cn(CELL_LINK, 'text-left whitespace-nowrap')}
                        to={`/expense/${expense.id}`}
                    >
                        <FormattedDate value={expense.expenseDate} />
                    </LinkWithOrigin>
                ),
            },
            {
                id: 'description',
                header: <FormattedMessage id="description" />,
                width: '18%',
                Cell: (expense) => (
                    <LinkWithOrigin
                        className={cn(CELL_LINK, 'text-left whitespace-normal')}
                        to={`/expense/${expense.id}`}
                    >
                        <LineClamp className="text-fg-secondary">{expense.description ?? ''}</LineClamp>
                    </LinkWithOrigin>
                ),
            },
            {
                id: 'status',
                header: <FormattedMessage id="expenses.grouped.status" />,
                width: '7%',
                Cell: (expense) => (
                    <div className="flex flex-row flex-wrap items-center gap-1">
                        {expense.isNonBillable ? (
                            <Chip
                                className="cursor-pointer"
                                size="small"
                                label={<MoneyOffRoundedIcon />}
                                type={'partyType'}
                            />
                        ) : null}
                        {expense.expenseStatusTypeCode && expenseStatusClassifier ? (
                            <ExpenseStatusLabel
                                classifier={expenseStatusClassifier}
                                statusCode={expense.expenseStatusTypeCode}
                                isNonBillable={expense.isNonBillable}
                            />
                        ) : null}
                        {expense.timecardId !== null ? (
                            <Tooltip content={t('expenses.opens-timecard')}>
                                <LinkWithOrigin
                                    className={CELL_LINK}
                                    to={`/timecards/${expense.timecardId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t('expenses.opens-timecard')}
                                >
                                    <TimecardLinkIcon />
                                </LinkWithOrigin>
                            </Tooltip>
                        ) : null}
                    </div>
                ),
            },
            {
                id: 'invoicePaidDate',
                header: <FormattedMessage id="expenses.grouped.invoice-paid-date" />,
                width: '9%',
                Cell: (expense) => {
                    const paidDate = getLatestConnectedInvoicePaidDate(expense.invoices);
                    return (
                        <div className="flex flex-col gap-0.5">
                            {expense.invoices.length > 0 ? (
                                <div>
                                    {getLinks(
                                        'invoice',
                                        expense.invoices.map(({ id, invoiceNumber }) => ({
                                            id,
                                            name: invoiceNumber,
                                        })),
                                        { target: '_blank' }
                                    )}
                                </div>
                            ) : null}
                            {paidDate ? (
                                <span className="text-fg-secondary text-xs">
                                    <FormattedDate value={paidDate} />
                                </span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                id: 'caseBdEffort',
                header: <FormattedMessage id="expenses.grouped.case-bd-effort" />,
                Cell: (expense) => (
                    <div className="flex flex-col gap-1">
                        {expense.matter != null ? (
                            <span className="new:flex-wrap flex items-center gap-1">
                                <MatterIndicators
                                    isConfidential={expense.matter.isConfidential}
                                    hasAccess={hasMatterAccess(expense.matter)}
                                    className={BOTTOM_LINE_IN_NEW_DESIGN}
                                />
                                <RecordLink
                                    className={CELL_LINK}
                                    to={`/matter/${expense.matter.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    label={expense.matter.fullName}
                                    withOrigin
                                />
                            </span>
                        ) : null}
                        {expense.effortId !== null ? (
                            <LinkWithOrigin
                                className={CELL_LINK}
                                to={`/effort/${expense.effortId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FormattedMessage id="expenses.bd-effort" />
                            </LinkWithOrigin>
                        ) : null}
                    </div>
                ),
            },
            {
                id: 'expenseAmount',
                header: <FormattedMessage id="expenses.amount" />,
                width: '14%',
                align: 'right',
                sortAccessor: (expense) => expense.expenseAmount,
                Cell: (expense) =>
                    expense.expenseAmount != null && expense.currencyTypeCode ? (
                        <MoneyDisplay amount={expense.expenseAmount} currency={expense.currencyTypeCode} />
                    ) : (
                        '-'
                    ),
            },
            {
                id: 'expenseAmountOriginalCurrency',
                header: <FormattedMessage id="expenses.amount-original-currency" />,
                width: '14%',
                align: 'right',
                sortAccessor: (expense) => expense.expenseAmountOriginalCurrency,
                Cell: (expense) =>
                    expense.expenseAmountOriginalCurrency != null && expense.originalCurrencyTypeCode ? (
                        <MoneyDisplay
                            amount={expense.expenseAmountOriginalCurrency}
                            currency={expense.originalCurrencyTypeCode}
                        />
                    ) : (
                        '-'
                    ),
            },
            {
                id: 'actions',
                header: '',
                width: '48px',
                align: 'right',
                Cell: (expense) => <ExpenseActionButtons expense={expense} onDuplicate={onDuplicate} />,
            },
        ],
        [expenseStatusClassifier, onDuplicate]
    );

    return (
        <NestedTable<ExpenseListItem>
            data={expenses}
            columns={columns}
            getRowKey={(expense) => expense.id}
            size="small"
            surface="deep"
        />
    );
};

export default ExpensesGroupedTier3Rows;
